

import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  TextField,
  CircularProgress,
  Alert
} from '@mui/material';
import MDButton from 'components/MDButton';
import DynamicField from '../components/FieldSelector/DynamicField';
import { useAuth } from 'auth/Auth';
import MetadataService from '../services/metadataService';

function DynamicObjectModal({ open, onClose, objectType, onSave }) {
  const { axiosInstance } = useAuth();
  const [formData, setFormData] = useState({});
  const [fields, setFields] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const metadataService = new MetadataService({ axiosInstance });



  useEffect(() => {
    if (open && objectType) {
      loadFields();
    }
  }, [open, objectType]);

  const loadFields = async () => {
    setLoading(true);
    try {
      const fieldsData = await metadataService.getFields(objectType);
      setFields(fieldsData.filter(f => !f.metadata?.auto_generated));
      
      // Initialize form data with default values
      const initialData = {};
      fieldsData.forEach(field => {
        if (field.metadata?.default_value) {
          initialData[field.id] = field.metadata.default_value;
        }
      });
      setFormData(initialData);
    } catch (error) {
      setError('Failed to load fields');
      console.error('Error loading fields:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFieldChange = (fieldId, value) => {
    setFormData(prev => ({
      ...prev,
      [fieldId]: value
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    
    try {
      // Validate required fields
      const errors = [];
      fields.forEach(field => {
        if (field.required && !formData[field.id]) {
          errors.push(`${field.label} is required`);
        }
      });
      
      if (errors.length > 0) {
        setError(errors.join(', '));
        setSaving(false);
        return;
      }

      // Determine the API endpoint
      const apiName = objectType.startsWith('co_') ? objectType.replace(/^co_/, '') : objectType;
      const endpoint = objectType.startsWith('co_')
        ? `/custom-objects/${apiName}/data`
        : `/${objectType}s/`;

      const response = await axiosInstance.post(endpoint, formData);
      
      if (onSave) {
        await onSave(response.data);
      }
      
      onClose();
    } catch (error) {
      setError(error.response?.data?.message || 'Failed to save record');
      console.error('Error saving record:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    if (!saving) {
      setFormData({});
      setError(null);
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        Create New {objectType?.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
      </DialogTitle>
      
      <DialogContent>
        {loading ? (
          <CircularProgress />
        ) : error ? (
          <Alert severity="error">{error}</Alert>
        ) : (
          <Grid container spacing={2} sx={{ mt: 1 }}>
            {fields.map(field => (
              <Grid item xs={12} md={field.type === 'textarea' ? 12 : 6} key={field.id}>
                <DynamicField
                  field={field}
                  value={formData[field.id] || ''}
                  onChange={(value) => handleFieldChange(field.id, value)}
                />
              </Grid>
            ))}
          </Grid>
        )}
      </DialogContent>
      
      <DialogActions>
        <MDButton onClick={handleClose} disabled={saving}>
          Cancel
        </MDButton>
        <MDButton
          variant="gradient"
          color="success"
          onClick={handleSave}
          disabled={saving || loading}
        >
          {saving ? 'Creating...' : 'Create'}
        </MDButton>
      </DialogActions>
    </Dialog>
  );
}

export default DynamicObjectModal;
